import { FormEventHandler, useCallback, useState } from 'react';

import { useCombobox, UseComboboxStateChange } from 'downshift';

import { useControlled } from '@adsk/alloy-react-helpers';

import type { GenericItem } from '../types';

/**
 * Workaround for IME support.
 *
 * The idea is to not fire `onInputValueChange` while IME is composing.
 * This breaks IME support by preventing the IME selection menu from appearing.
 * This hook manages inputValue state and only fires downshift's input `onChange` event when IME composition completes (when an option is selected from the IME menu).
 *
 * See these issues:
 * @see https://github.com/downshift-js/downshift/issues/1108#issuecomment-674180157
 * @see https://github.com/facebook/react/issues/3926#issuecomment-929799564
 */
export default <TItem extends GenericItem>({
  inputValueProp,
  initialInputValue,
  onInputValueChangeProp,
}: {
  inputValueProp: string | undefined;
  initialInputValue: string | undefined;
  onInputValueChangeProp:
    | ((args: UseComboboxStateChange<TItem>) => void)
    | undefined;
}) => {
  const isControlled = inputValueProp !== undefined;

  const [isComposing, setIsComposing] = useState(false);

  const [inputValue, setInputValue] = useControlled<string>(
    initialInputValue ?? '',
    isComposing ? undefined : inputValueProp,
  );

  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  const getOnCompositionEnd = useCallback(
    ({ onChangeProp }: { onChangeProp?: FormEventHandler<HTMLInputElement> }) =>
      (event: React.CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        onChangeProp?.(event);
      },
    [],
  );

  const onInputValueChange = useCallback(
    (args: UseComboboxStateChange<TItem>) => {
      if (
        // This type gets triggered on render in multi autocomplete scenarios which provide selectedItem as null
        // This sets the input value to '' and breaks `initialInputValue`
        args.type !==
        useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem
      ) {
        if (!isControlled || isComposing) {
          // controlled inputValue should take priority
          setInputValue(args.inputValue ?? '');
        }
      }

      if (isComposing) {
        // IME composition is in progress, do not update input value
        return;
      }

      onInputValueChangeProp?.(args);
    },
    [isComposing, onInputValueChangeProp, isControlled, setInputValue],
  );

  return {
    handleCompositionStart,
    getOnCompositionEnd,
    onInputValueChange,
    inputValue,
    setInputValue,
  };
};
